/**
 * EventTracking Service
 * Provides centralised event tracking for all block actions
 */

class EventTrackingService {
  constructor() {
    this.config = {
      providers: {
        gtag: {
          enabled: true,
          checkFunction: () => typeof window.gtag !== 'undefined'
        },
        dataLayer: {
          enabled: true, 
          checkFunction: () => typeof window.dataLayer !== 'undefined'
        },
        fbPixel: {
          enabled: true,
          checkFunction: () => typeof window.fbq !== 'undefined'
        }
      },
      defaultEventCategory: 'interaction'
    };

    this.init();
  }

  init() {
    // Auto-track page views when ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.trackPageView());
    } else {
      this.trackPageView();
    }

    // Make tracking functions globally available
    this.setupGlobalAPI();
    
    this.debugLog('Service initialized');
  }

  /**
   * Set up global API for backward compatibility
   */
  setupGlobalAPI() {
    if (typeof window !== 'undefined') {
      window.ArcEventTracking = {
        track: this.trackEvent.bind(this),
        trackAction: this.trackActionEvent.bind(this),
        trackForm: this.trackFormSubmission.bind(this),
        trackPageView: this.trackPageView.bind(this),
        configure: this.configureTracking.bind(this)
      };
    }
  }

  /**
   * Log debug information if enabled
   * @param {string} message - Debug message
   * @param {any} data - Additional data to log
   */
  debugLog(message, data = null) {
    if (window.enableDebugMode) {
      console.log(`🔍 [Event Tracking] ${message}`, data || '');
    }
  }

  /**
   * Track an event across all configured providers
   * @param {object} eventData - Event data object
   * @param {string} eventData.eventName - Name of the event
   * @param {string} [eventData.eventCategory] - Event category (defaults to 'interaction')
   * @param {string} [eventData.eventLabel] - Event label
   * @param {number|string} [eventData.eventValue] - Event value
   * @param {object} [eventData.customParameters] - Custom parameters for the event
   * @param {object} [eventData.blockSettings] - Block settings for contextual data
   * @param {string} [eventData.blockType] - Type of block triggering the event
   * @param {string} [eventData.blockId] - ID of the block triggering the event
   * @param {string} [eventData.actionType] - Type of action (click, hover, etc.)
   * @param {any} [eventData.actionValue] - Value from the action (input value, etc.)
   */
  trackEvent(eventData) {
    const {
      eventName,
      eventCategory = this.config.defaultEventCategory,
      eventLabel,
      eventValue,
      customParameters = {},
      blockSettings = {},
      blockType,
      blockId,
      actionType,
      actionValue
    } = eventData;

    // Validate required fields
    if (!eventName) {
      console.warn('Event tracking: eventName is required');
      return;
    }

    this.debugLog('Tracking event', eventData);

    // Enhanced event data with context
    const enhancedEventData = {
      event: eventName,
      event_category: eventCategory,
      event_label: eventLabel,
      event_value: eventValue,
      block_type: blockType,
      block_id: blockId,
      action_type: actionType,
      action_value: actionValue,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),
      ...customParameters
    };

    // Track with Google Analytics (gtag)
    if (this.config.providers.gtag.enabled && this.config.providers.gtag.checkFunction()) {
      try {
        window.gtag('event', eventName, {
          event_category: eventCategory,
          event_label: eventLabel,
          value: eventValue,
          custom_parameter_1: actionValue,
          block_type: blockType,
          block_id: blockId,
          action_type: actionType,
          ...customParameters
        });
        this.debugLog('Sent to Google Analytics', enhancedEventData);
      } catch (error) {
        console.error('Error tracking with Google Analytics:', error);
      }
    }

    // Track with Google Tag Manager (dataLayer)
    if (this.config.providers.dataLayer.enabled && this.config.providers.dataLayer.checkFunction()) {
      try {
        window.dataLayer.push(enhancedEventData);
        this.debugLog('Sent to Google Tag Manager', enhancedEventData);
      } catch (error) {
        console.error('Error tracking with Google Tag Manager:', error);
      }
    }

    // Track with Facebook Pixel
    if (this.config.providers.fbPixel.enabled && this.config.providers.fbPixel.checkFunction()) {
      try {
        window.fbq('track', eventName, {
          content_category: eventCategory,
          content_name: eventLabel,
          value: eventValue,
          currency: 'AUD',
          ...customParameters
        });
        this.debugLog('Sent to Facebook Pixel', enhancedEventData);
      } catch (error) {
        console.error('Error tracking with Facebook Pixel:', error);
      }
    }
  }

  /**
   * Track action events automatically from block actions
   * @param {object} actionEventDetail - Event detail from element-action dispatch
   */
  trackActionEvent(actionEventDetail) {
    const { blockId, settings, triggerType, element } = actionEventDetail;

    // Skip if tracking is not enabled
    if (!settings.enable_event_tracking && !settings.enable_actions) {
      return;
    }

    // Get block type from element classes or data attributes
    const blockType = element?.classList?.value?.match(/(\w+)-block/)?.[1] || 'unknown';

    // Get action value if available (for inputs, selections, etc.)
    let actionValue = null;
    if (element) {
      const input = element.querySelector('input, select, textarea');
      if (input) {
        actionValue = input.value;
      }
    }

    // Use block settings for event details, with sensible defaults
    const eventData = {
      eventName: settings.event_name || `${blockType}_${triggerType}`,
      eventCategory: settings.event_category || this.config.defaultEventCategory,
      eventLabel: settings.event_label || `${blockType} - ${triggerType}`,
      eventValue: settings.event_value || null,
      blockSettings: settings,
      blockType,
      blockId,
      actionType: triggerType,
      actionValue,
      customParameters: {
        input_name: settings.input_name,
        input_type: settings.input_type,
        action_preset: settings.action_preset,
        button_label: settings.label
      }
    };

    this.trackEvent(eventData);
  }

  /**
   * Track form submission events
   * @param {object} formData - Form submission data
   */
  trackFormSubmission(formData) {
    const { formId, formType, fieldCount, submissionTime, formData: data } = formData;

    this.trackEvent({
      eventName: 'form_submission',
      eventCategory: 'form',
      eventLabel: formId || formType,
      eventValue: fieldCount,
      customParameters: {
        form_id: formId,
        form_type: formType,
        field_count: fieldCount,
        submission_time: submissionTime,
        form_data: JSON.stringify(data)
      }
    });
  }

  /**
   * Track page view events
   * @param {object} pageData - Page view data
   */
  trackPageView(pageData = {}) {
    const { pageTitle, pageUrl, pageType, customData } = pageData;

    this.trackEvent({
      eventName: 'page_view',
      eventCategory: 'navigation',
      eventLabel: pageTitle || document.title,
      customParameters: {
        page_url: pageUrl || window.location.href,
        page_title: pageTitle || document.title,
        page_type: pageType,
        ...customData
      }
    });
  }

  /**
   * Configure tracking providers
   * @param {object} config - Configuration object
   */
  configureTracking(config) {
    Object.assign(this.config, config);
    this.debugLog('Tracking configuration updated', this.config);
  }
}

// Auto-initialize the service
const eventTrackingService = new EventTrackingService();

// Export the service instance and class
export default eventTrackingService;
export { EventTrackingService };